import { useEffect, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';

const SCRIPT_ID = 'json-ld-schema';

/**
 * JsonLd component - manages a <script type="application/ld+json"> tag in the document head.
 *
 * On initial mount, it hydrates from an existing server-rendered script tag (if present).
 * On subsequent route changes, it updates or replaces the script content.
 *
 * Props:
 *   schema - object or array of objects to serialize as JSON-LD (pass null to remove)
 *
 * Usage:
 *   <JsonLd schema={{ "@context": "https://schema.org", "@type": "WebPage", name: "..." }} />
 *   <JsonLd schema={[schema1, schema2]} />
 */
function JsonLdComponent({ schema, location }) {
  const scriptRef = useRef(null);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    // On first mount, check for existing server-rendered script tag
    if (!isHydrated) {
      const existing = document.querySelector(`script[type="application/ld+json"]`);
      if (existing) {
        scriptRef.current = existing;
        // Ensure it has our ID for future management
        existing.id = SCRIPT_ID;
      }
      setIsHydrated(true);
      return;
    }

    // After hydration, manage the script tag based on schema prop
    if (!schema) {
      if (scriptRef.current) {
        scriptRef.current.remove();
        scriptRef.current = null;
      }
      return;
    }

    const jsonString = JSON.stringify(schema);

    if (!scriptRef.current) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.type = 'application/ld+json';
      script.textContent = jsonString;
      document.head.appendChild(script);
      scriptRef.current = script;
    } else {
      scriptRef.current.textContent = jsonString;
    }
  }, [schema, location.pathname, isHydrated]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scriptRef.current) {
        scriptRef.current.remove();
        scriptRef.current = null;
      }
    };
  }, []);

  return null;
}

export default withRouter(JsonLdComponent);
